import { useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Initials, Notice, Spinner } from '../components/ui';
import { api } from '../lib/api';
import './Page.css';
import './Shared.css';
import './Hub.css';
import { BackLink } from '../components/BackLink';

interface SharedRow {
  id: string;
  class_name: string | null;
  framework_name: string | null;
  created_at: string;
  shared_at: string;
  teacher_id: string;
  teacher_name: string;
  is_own: number;
}

const dateFmt = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** Reports teachers have chosen to share with you (and your own). Nothing else is visible to leaders. */
export function SharedPage() {
  const shared = useQuery({
    queryKey: ['shared'],
    queryFn: async () => (await api<{ data: SharedRow[] }>('/schools/mine/shared-sessions')).data,
  });
  const rows = shared.data ?? [];
  // ?staff=<id>: one person's shared reports (from a name in the Leadership Hub).
  const [params, setParams] = useSearchParams();
  const staff = params.get('staff');
  const everyone = rows.filter((r) => !Number(r.is_own));
  const people = [...new Map(everyone.map((r) => [r.teacher_id, r.teacher_name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const fromOthers = staff ? everyone.filter((r) => r.teacher_id === staff) : everyone;
  const pickStaff = (id: string) => setParams(id ? { staff: id } : {}, { replace: true });
  const own = rows.filter((r) => Number(r.is_own));

  return (
    <div className="page">
      <header className="page-head">
        <h1>Shared with you</h1>
        <p className="muted">Lesson reports your staff have chosen to share with you. Everything else stays private to them.</p>
      </header>
      {shared.isLoading ? (
        <Spinner />
      ) : shared.isError ? (
        <Notice tone="error">We couldn&rsquo;t load shared reports. Refresh the page to try again.</Notice>
      ) : (
        <>
          {people.length > 1 ? (
            <label className="hub-select shared-staff">
              <span>Staff member</span>
              <select className="select" value={staff ?? ''} onChange={(e) => pickStaff(e.target.value)}>
                <option value="">Everyone</option>
                {people.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {staff && !fromOthers.length && everyone.length ? (
            <section className="panel shared-empty">
              <p className="muted">Nothing from this person is shared with you.</p>
              <button type="button" className="hub-link" onClick={() => pickStaff('')}>
                Show everyone
              </button>
            </section>
          ) : fromOthers.length ? (
            <ReportList rows={fromOthers} sortable />
          ) : (
            <section className="panel shared-empty">
              <h2>Nothing shared with you yet</h2>
              <p className="muted">Reports are private unless the person who recorded them chooses to share. When they do, they appear here.</p>
              <ol className="shared-steps">
                <li>
                  <strong>Record a lesson</strong> in the EducAitor app.
                </li>
                <li>
                  <strong>Open the report</strong> when it&rsquo;s ready.
                </li>
                <li>
                  <strong>Tap Share</strong> and choose who can see it.
                </li>
              </ol>
            </section>
          )}
          {own.length ? (
            <section className="page">
              <h2>Your own lessons</h2>
              <ReportList rows={own} />
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

type SortKey = 'shared' | 'session' | 'name';

/** Staff member, session date, framework and when it was shared; sorted by shared date first (N-05). */
function ReportList({ rows, sortable }: { rows: SharedRow[]; sortable?: boolean }) {
  const [sort, setSort] = useState<SortKey>('shared');
  if (rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) =>
    sort === 'name'
      ? a.teacher_name.localeCompare(b.teacher_name)
      : sort === 'session'
        ? +new Date(b.created_at) - +new Date(a.created_at)
        : +new Date(b.shared_at) - +new Date(a.shared_at),
  );
  return (
    <div className="page">
      {sortable ? (
        <div className="shared-sort" role="group" aria-label="Sort by">
          <span className="faint">Sort by</span>
          {(
            [
              ['shared', 'Shared date'],
              ['session', 'Lesson date'],
              ['name', 'Staff member'],
            ] as [SortKey, string][]
          ).map(([k, label]) => (
            <button key={k} type="button" className="choice-chip" aria-pressed={sort === k} onClick={() => setSort(k)}>
              {label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="panel">
        <div className="list">
          {sorted.map((r) => (
            <Link key={r.id} to={`/shared/${r.id}`} state={{ teacher: r.teacher_name, className: r.class_name }} className="list-row report-row">
              <Initials name={r.teacher_name} />
              <div className="list-main">
                <p className="list-title">{r.teacher_name}</p>
                <p className="list-sub">
                  {r.class_name ?? 'Lesson'} · {dateFmt(r.created_at)}
                  {r.framework_name ? ` · ${r.framework_name}` : ''}
                </p>
              </div>
              <span className="meta">{Number(r.is_own) ? '' : `Shared ${dateFmt(r.shared_at)}`}</span>
              <span className="report-open">Open</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

interface Indicator {
  id: string;
  indicatorName: string;
  /** Null: not seen this lesson (feedback is then what to listen for). */
  score: number | string | null;
  seen?: boolean;
  feedback: string;
  recommendations: string[];
}
interface Domain {
  id: string;
  domainName: string;
  score: number | string | null;
  seen?: boolean;
  summary: string;
  indicators: Indicator[];
}
interface Detail {
  session: { id: string; created_at: string; duration_seconds: number | null; framework_name: string | null; class_name: string | null };
  evaluation: { overall_score: number | string | null; overall_summary: string; domains: Domain[] } | null;
  /** Trends only (backend services/scoreMode.ts): no numbers; `seen` says whether an area or indicator came up. */
  scoreMode?: 'full' | 'team' | 'trends';
  scoresHidden?: boolean;
  /** What the teacher was working on, when they chose to share it. */
  focus?: { text: string; indicatorName: string | null; observation: string | null } | null;
}

/** A shared lesson report, read-only. */
export function SharedReportPage() {
  const { id } = useParams();
  const state = useLocation().state as { teacher?: string; className?: string | null } | null;
  const detail = useQuery({ queryKey: ['report', id], queryFn: () => api<Detail>(`/sessions/${id}`), enabled: Boolean(id) });

  if (detail.isLoading) return <Spinner />;
  if (detail.isError || !detail.data) return <Notice tone="error">This report isn&rsquo;t available. It may no longer be shared with you.</Notice>;
  const { session, evaluation } = detail.data;
  // Trends only: no numbers, even on a leader's own lessons.
  const hide = detail.data.scoresHidden || detail.data.scoreMode === 'trends';
  const seen = (x: { score: number | string | null; seen?: boolean }) => x.seen ?? x.score != null;
  const minutes = session.duration_seconds ? Math.round(session.duration_seconds / 60) : null;

  return (
    <div className="page report">
      <BackLink to="/shared">Shared with you</BackLink>
      <header className="page-head">
        <p className="eyebrow">{state?.teacher ?? 'Shared report'}</p>
        <h1>{session.class_name ?? state?.className ?? 'Lesson'}</h1>
        <p className="muted">
          {dateFmt(session.created_at)}
          {minutes ? ` · ${minutes} min` : ''}
          {session.framework_name ? ` · ${session.framework_name}` : ''}
        </p>
      </header>

      {!evaluation ? (
        <Notice>This lesson doesn&rsquo;t have a report.</Notice>
      ) : (
        <>
          <section className="panel panel-tint report-summary">
            {hide || evaluation.overall_score == null ? null : (
              <div className="report-score">
                <span className="report-score-value">{Number(evaluation.overall_score).toFixed(1)}</span>
                <span className="muted">overall</span>
              </div>
            )}
            <p>{evaluation.overall_summary}</p>
          </section>

          {detail.data.focus ? (
            <section className="panel report-focus">
              <p className="eyebrow">Working on</p>
              <h2>{detail.data.focus.text}</h2>
              {detail.data.focus.indicatorName ? <p className="muted">{detail.data.focus.indicatorName}</p> : null}
              {detail.data.focus.observation ? (
                <div className="report-next">
                  <span className="eyebrow">What the report noticed</span>
                  <p>{detail.data.focus.observation}</p>
                </div>
              ) : null}
            </section>
          ) : null}

          {evaluation.domains.map((d) => (
            <section key={d.id} className="panel report-domain">
              <div className="report-domain-head">
                <h2>{d.domainName}</h2>
                {!seen(d) ? (
                  <span className="report-not-seen">Not seen</span>
                ) : hide ? null : (
                  <span className="report-domain-score">{Number(d.score).toFixed(1)}</span>
                )}
              </div>
              {d.summary ? <p className="muted">{d.summary}</p> : null}
              <div className="report-indicators">
                {d.indicators.map((i) => (
                  <div key={i.id} className="report-indicator">
                    <div className="report-indicator-head">
                      <h3>{i.indicatorName}</h3>
                      {!seen(i) ? (
                        <span className="report-not-seen">Not seen this lesson</span>
                      ) : hide ? null : (
                        <span className="tabular">{Number(i.score).toFixed(1)}</span>
                      )}
                    </div>
                    <p className={!seen(i) ? 'muted' : undefined}>{i.feedback}</p>
                    {i.recommendations?.length ? (
                      <div className="report-next">
                        <span className="eyebrow">Try next</span>
                        <ul>
                          {i.recommendations.map((r, n) => (
                            <li key={n}>{r}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
